/**
 * Product info tabs (ported from the Horizon WORK theme).
 * Splits the description by its headings into tabs (optional), adds Delivery / Returns / Reviews tabs.
 * Desktop: tabs. Phones: accordion. Without JS everything shows as plain content.
 */
if (!customElements.get('product-tabs')) {
  class ProductTabs extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = '1';
      this.init();
    }

    init() {
    const root = this;

    const bar = root.querySelector('[data-ptabs-bar]');
    const panelsWrap = root.querySelector('[data-ptabs-panels]');
    const source = root.querySelector('[data-ptabs-source]');
    const statics = [...root.querySelectorAll('[data-ptabs-static]')];
    const uid = `ptab-${root.dataset.sectionId}`;
    const sections = [];

    const clean = (t) => String(t || '').replace(/\s+/g, ' ').replace(/[:：]\s*$/, '').trim();
    const isEmpty = (nodes) => nodes.every((n) => !(n.textContent || '').trim() && !(n.querySelector && n.querySelector('img,iframe,video,table')));

    // Is this node a heading? h1–h4, or a short paragraph that is only bold text.
    const headingText = (node) => {
      if (node.nodeType !== 1) return null;
      if (/^H[1-4]$/.test(node.tagName)) {
        const t = clean(node.textContent);
        return t && t.length <= 70 ? t : null;
      }
      if (node.tagName === 'P') {
        const text = (node.textContent || '').trim();
        if (!text || text.length > 60) return null;
        const bold = [...node.querySelectorAll('strong,b')].map((b) => b.textContent).join('').trim();
        if (bold && bold.replace(/\s+/g, ' ') === text.replace(/\s+/g, ' ') && !text.startsWith('·')) return clean(text);
      }
      return null;
    };

    // "Label: value" lines → specification table
    const specRow = (line) => {
      const m = String(line).replace(/\u00a0/g, ' ').replace(/^[\s·•\-–]+/, '').match(/^([^:]{1,45}):\s*(.+)$/);
      return m ? [m[1].trim(), m[2].trim()] : null;
    };
    const linesOf = (el) => {
      const lines = [];
      el.querySelectorAll('li').forEach((li) => lines.push(li.textContent));
      if (lines.length) return lines;
      return el.innerHTML
        .split(/<br\s*\/?>/i)
        .map((h) => { const d = document.createElement('div'); d.innerHTML = h; return d.textContent; });
    };
    const enhanceSpecs = (body) => {
      [...body.children].forEach((el) => {
        if (!/^(P|UL|OL)$/.test(el.tagName)) return;
        const lines = linesOf(el).map((l) => l.trim()).filter(Boolean);
        if (lines.length < 2) return;
        const rows = lines.map(specRow);
        const hits = rows.filter(Boolean).length;
        if (hits < 2 || hits / lines.length < 0.7) return;
        const table = document.createElement('table');
        table.className = 'ptabs__specs';
        const tbody = document.createElement('tbody');
        lines.forEach((line, i) => {
          const tr = document.createElement('tr');
          if (rows[i]) {
            const th = document.createElement('th');
            th.scope = 'row';
            th.textContent = rows[i][0];
            const td = document.createElement('td');
            td.textContent = rows[i][1];
            tr.append(th, td);
          } else {
            const td = document.createElement('td');
            td.colSpan = 2;
            td.textContent = line.replace(/^[\s·•]+/, '');
            tr.append(td);
          }
          tbody.append(tr);
        });
        table.append(tbody);
        el.replaceWith(table);
      });
    };

    // 1. Split the description by its headings
    if (source) {
      let current = { title: source.dataset.overviewTitle || 'Overview', nodes: [] };
      [...source.childNodes].forEach((node) => {
        if (node.nodeType === 3 && !node.textContent.trim()) return;
        if (node.nodeType === 1 && node.tagName === 'META') return;
        const h = headingText(node);
        if (h) {
          if (!isEmpty(current.nodes)) sections.push(current);
          current = { title: h, nodes: [] };
        } else {
          current.nodes.push(node);
        }
      });
      if (!isEmpty(current.nodes)) sections.push(current);
      // Merge tiny duplicate titles (e.g. two "Key Features" headings)
      for (let i = sections.length - 1; i > 0; i--) {
        if (sections[i].title.toLowerCase() === sections[i - 1].title.toLowerCase()) {
          sections[i - 1].nodes.push(...sections[i].nodes);
          sections.splice(i, 1);
        }
      }
    }
    // Too many tabs is worse than one long tab: keep at most 5 description tabs
    while (sections.length > 5) {
      const last = sections.pop();
      const h = document.createElement('h3');
      h.textContent = last.title;
      sections[sections.length - 1].nodes.push(h, ...last.nodes);
    }
    statics.forEach((el) =>
      sections.push({
        title: el.dataset.title,
        nodes: [...el.childNodes],
        isStatic: true,
        isReviews: el.hasAttribute('data-ptabs-reviews'),
      })
    );
    if (!sections.length) return;

    // 2. Build tabs + panels (desktop tabs, mobile accordion)
    const panels = [];
    const tabs = [];
    const frag = document.createDocumentFragment();
    sections.forEach((s, i) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'ptabs__tab';
      tab.id = `${uid}-tab-${i}`;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', `${uid}-panel-${i}`);
      tab.textContent = s.title;
      bar.append(tab);
      tabs.push(tab);

      const panel = document.createElement('div');
      panel.className = 'ptabs__panel';
      panel.id = `${uid}-panel-${i}`;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      panel.tabIndex = 0;

      const acc = document.createElement('button');
      acc.type = 'button';
      acc.className = 'ptabs__acc';
      acc.setAttribute('aria-expanded', 'false');
      acc.innerHTML = '<span></span><span class="ptabs__acc-icon" aria-hidden="true"></span>';
      acc.firstChild.textContent = s.title;

      const body = document.createElement('div');
      body.className = 'ptabs__body rte';
      // Keep a real heading for each tab so the page outline stays intact for Google and screen readers
      const h2 = document.createElement('h2');
      h2.className = 'visually-hidden';
      h2.textContent = s.title;
      body.append(h2);
      s.nodes.forEach((n) => body.append(n));
      // Remove empty paragraphs / stray tags that leave blank gaps
      // Reviews panel is left untouched: Judge.me's stars are empty spans that it styles and fills in
      if (!s.isReviews) {
        body.querySelectorAll('meta').forEach((m) => m.remove());
        body.querySelectorAll('p, div, span, h3, h4').forEach((el) => {
          if (!el.textContent.trim() && !el.querySelector('img, iframe, video, table, svg')) el.remove();
        });
        body.querySelectorAll('p > br:last-child').forEach((br) => br.remove());
      }
      if (!s.isStatic) enhanceSpecs(body);

      panel.append(acc, body);
      frag.append(panel);
      panels.push(panel);

      acc.addEventListener('click', () => {
        const open = !panel.classList.contains('is-open');
        panel.classList.toggle('is-open', open);
        acc.setAttribute('aria-expanded', open);
      });
    });

    panelsWrap.innerHTML = '';
    panelsWrap.append(frag);
    bar.hidden = false;
    root.classList.add('is-ready');

    const select = (i, focus) => {
      tabs.forEach((t, n) => {
        const on = n === i;
        t.setAttribute('aria-selected', on);
        t.tabIndex = on ? 0 : -1;
        panels[n].classList.toggle('is-active', on);
      });
      if (focus) tabs[i].focus();
    };
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => select(i));
      t.addEventListener('keydown', (e) => {
        const map = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 };
        if (!(e.key in map)) return;
        e.preventDefault();
        select((map[e.key] + tabs.length) % tabs.length, true);
      });
    });
    // Other blocks (e.g. trust panel "Delivery details") can open a tab by name
    window.addEventListener('ptabs:open', (e) => {
      const want = String((e.detail && e.detail.title) || '').toLowerCase();
      const i = sections.findIndex((s) => s.title.toLowerCase() === want);
      if (i < 0) return;
      select(i);
      panels.forEach((p, n) => {
        const open = n === i;
        p.classList.toggle('is-open', open || p.classList.contains('is-open'));
        if (open) p.querySelector('.ptabs__acc').setAttribute('aria-expanded', 'true');
      });
      const target = window.matchMedia('(max-width: 749px)').matches ? panels[i] : root;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    // Clicking the star rating under the product title opens the Reviews tab
    const reviewsIndex = sections.findIndex((s) => s.isReviews);
    if (reviewsIndex > -1) {
      document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-ptabs-reviews-link], .jdgm-prev-badge')) return;
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('ptabs:open', { detail: { title: sections[reviewsIndex].title } }));
      });
    }

    select(0);
    panels[0].classList.add('is-open');
    panels[0].querySelector('.ptabs__acc').setAttribute('aria-expanded', 'true');
    }
  }

  customElements.define('product-tabs', ProductTabs);
}
